import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar } from '../components/ui/calendar';
import { engineers, generateTimeSlots } from '../mock';
import { Clock, User, DollarSign, CheckCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackBookingStart, trackBundleInquiry, trackPWABooking, trackPWADiscountApplied } from '../utils/analytics';
import CreditsCheckout from './CreditsCheckout';
import { trackFunnel, trackPurchaseOnce } from '../utils/bookingAnalytics';
import { BOOKING_DEPOSIT, MIN_SESSION_HOURS, BALANCE_DUE_NOTE, depositFor } from '../lib/bookingRules';

const API = '/api';

const BookingSection = ({ hideHeader = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date(new Date().setHours(0, 0, 0, 0)));
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timeSlots] = useState(generateTimeSlots());
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [promoStatus, setPromoStatus] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  // Per-addon quantities (only used by addons flagged `quantifiable: true`).
  // Shape: { [addonId]: qty }
  const [addonQuantities, setAddonQuantities] = useState({});
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    referralCode: '',
    smsConsent: false
  });
  const [availableCredits, setAvailableCredits] = useState(0);

  // Check if user is logged in
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user_data');
      if (userData && userData !== 'undefined' && userData !== 'null') {
        const parsed = JSON.parse(userData);
        if (parsed && parsed.email) {
          setUser(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, []);
  const [pwaDiscountAvailable, setPwaDiscountAvailable] = useState(false);
  const [appliedCredits, setAppliedCredits] = useState(null);
  const [bonusHoursDeal, setBonusHoursDeal] = useState(null);
  const dealFreeHours = (hours) => {
    if (!hours) return 0;
    let best = 0;
    const d = bonusHoursDeal;
    const ok = (ids) => !(ids && ids.length) || ids.includes(selectedEngineer);
    if (d?.is_active && ok(d.engineer_ids)) {
      best = Math.max(0, ...(d.tiers || []).filter(t => hours >= Number(t.min_hours || 0)).map(t => Number(t.free_hours || 0)));
    }
    const wk = d?.weekday_deal;
    const pyWeekday = selectedDate ? (selectedDate.getDay() + 6) % 7 : -1;
    if (wk && ok(wk.engineer_ids) && hours >= Number(wk.min_hours || 3) && (wk.weekdays || []).includes(pyWeekday)) {
      best = Math.max(best, Number(wk.free_hours || 1));
    }
    return best;
  };
  useEffect(() => { setAppliedCredits(null); }, [formData.email]);
  const [artistPlan, setArtistPlan] = useState(null);
  const [usePlanHours, setUsePlanHours] = useState(false);
  useEffect(() => {
    const t = localStorage.getItem('auth_token');
    if (!t) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => setArtistPlan(r.data?.has_plan ? r.data : null)).catch(() => setArtistPlan(null));
  }, []);
  const planUsable = !!artistPlan && selectedEngineer && selectedEngineer !== 1 && selectedDuration &&
    Number(artistPlan.hours_left || 0) >= selectedDuration && selectedAddons.length === 0 &&
    (JSON.parse(localStorage.getItem('user_data') || '{}').email || '').toLowerCase() === (formData.email || '').trim().toLowerCase();
  useEffect(() => { if (!planUsable && usePlanHours) setUsePlanHours(false); }, [planUsable, usePlanHours]);
  const [paymentType, setPaymentType] = useState('deposit'); // 'deposit' or 'full'
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState(null); // { valid: true, discount_percentage: 20 }
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountResult, setDiscountResult] = useState(null); // { valid: true, discount_percent: 10, ... }
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [packageFormData, setPackageFormData] = useState({
    name: '',
    email: '',
    phone: '',
    requestedDate: new Date(Date.now() + 86400000).toISOString().split('T')[0], // Tomorrow by default
    requestedTime: '',
    notes: ''
  });
  const [isPackageSubmitting, setIsPackageSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Separate, OPTIONAL SMS opt-in. Required by carrier A2P 10DLC compliance:
  // SMS consent must not be a condition of completing a booking.
  const [agreedToSms, setAgreedToSms] = useState(false);

  // Available add-ons — dynamic based on selected engineer.
  // - Reel add-on: available to everyone
  // - Wick custom beats ($50 each, qty selectable): only when Wick (id=7) is selected
  // - PB Slimeyy $150 discounted feature: only when PB Slimeyy (id=6) is selected
  const addons = useMemo(() => {
    const base = [
      {
        id: 1,
        name: 'Reel Add-On',
        price: 50,
        description: 'Professional reel shot in the last 15 minutes of your session',
        hasInfo: true,
        infoText: 'Your reel can be a song recorded during this session OR a previously recorded song shot inside the studio. Perfect for Instagram, TikTok, and promotional content!'
      }
    ];
    if (selectedEngineer === 7) {
      base.push({
        id: 10,
        name: 'Custom Wick Beat',
        price: 50,
        description: 'Original beat produced by Wick, delivered live in-session. Yours to keep.',
        hasInfo: true,
        quantifiable: true,
        maxQty: 10,
        infoText: 'Wick will craft custom instrumentals during your session, tailored to your vibe. You walk out with WAV + stems for each. Use +/− to add multiple in one checkout.'
      });
    }
    if (selectedEngineer === 6) {
      base.push({
        id: 11,
        name: 'PB Feature Verse',
        price: 150,
        description: 'PB records a verse on your track. Site-only price.',
        compact: true
      });
    }
    return base;
  }, [selectedEngineer]);

  // Auto-fill form data from logged-in user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        try {
          let user = JSON.parse(userData);
          
          // If user doesn't have profile fields (artist_name, etc.), fetch them
          if (!user.artist_name && !user.about_me) {
            try {
              const profileResponse = await axios.get(`${API}/users/profile/${user.email}`);
              if (profileResponse.data.success) {
                user = { ...user, ...profileResponse.data.user };
                // Update localStorage with complete profile data
                localStorage.setItem('user_data', JSON.stringify(user));
                console.log('✅ Fetched and cached complete profile data');
              }
            } catch (error) {
              console.log('No additional profile data available');
            }
          }
          
          // Format name based on privacy preference
          let displayName = '';
          if (user.artist_name && user.full_name && !user.keep_name_private) {
            displayName = `${user.artist_name} (${user.full_name})`;
          } else if (user.artist_name) {
            displayName = user.artist_name;
          } else if (user.full_name) {
            displayName = user.full_name;
          } else if (user.name) {
            displayName = user.name;
          }
          
          // Pre-fill form data — logged-in users count as already-consented to SMS (TCPA)
          setFormData(prev => ({
            ...prev,
            name: displayName || prev.name,
            email: user.email || prev.email,
            phone: user.phone || prev.phone,
            smsConsent: true
          }));
          
          // Pre-fill package form data too
          setPackageFormData(prev => ({
            ...prev,
            name: displayName || prev.name,
            email: user.email || prev.email,
            phone: user.phone || prev.phone
          }));
          
          console.log('✅ Auto-filled booking form with user profile data:', { displayName, email: user.email, phone: user.phone });
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      }
    };
    
    loadUserProfile();
  }, []);

  // Fetch promo status on component mount
  useEffect(() => {
    const fetchPromoStatus = async () => {
      try {
        const response = await axios.get(`${API}/promo-status`);
        setPromoStatus(response.data);
      } catch (error) {
        console.error('Error fetching promo status:', error);
      }
    };
    fetchPromoStatus();

    // Check for payment success
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const refParam = (urlParams.get('ref') || '').trim().toUpperCase();
    if (refParam) {
      localStorage.setItem('hq_ref', refParam);
    }
    const savedRef = refParam || localStorage.getItem('hq_ref');
    if (savedRef) {
      setFormData(prev => (prev.referralCode ? prev : { ...prev, referralCode: savedRef }));
    }
    
    if (paymentStatus === 'success') {
      // Payment was successful - the Stripe webhook has already created the booking
      // Just show success message and clean up localStorage
      const pendingBooking = localStorage.getItem('pendingBooking');
      if (pendingBooking) {
        const bookingData = JSON.parse(pendingBooking);
        
        trackPurchaseOnce(urlParams.get('session_id') || bookingData.ga_txn, {
          package_name: bookingData.ga_package || 'Recording Session',
          booking_value: bookingData.price,
          value: Number(bookingData.ga_amount_paid ?? bookingData.price) || 0,
          payment_type: bookingData.ga_payment_type || 'unknown',
        });
        
        // Credits used at checkout are deducted server-side by the Stripe webhook
        localStorage.removeItem('pendingCreditsRedemption');
        
        // Mark discount code as used
        const pendingDiscount = localStorage.getItem('pendingDiscountCode');
        if (pendingDiscount) {
          const { code, email } = JSON.parse(pendingDiscount);
          axios.post(`${API}/discount/apply`, {
            code: code,
            email: email,
            booking_id: bookingData.id || ''
          }).then(() => {
            localStorage.removeItem('pendingDiscountCode');
          }).catch((error) => {
            console.error('Error applying discount code:', error);
          });
        }
        
        localStorage.removeItem('pendingBooking');
        const toastMsg = bookingData.ga_payment_type === 'deposit'
          ? `Booking received! $${Number(bookingData.ga_amount_paid || 0).toFixed(2)} deposit paid - the rest is due at the studio on session day. Check your email.`
          : 'Booking received! Payment complete. Check your email for confirmation.';
        setTimeout(() => toast.success(toastMsg), 800);
      } else {
        // No pending booking data but payment success - still show success
        setTimeout(() => toast.success('Payment received! Check your email for booking confirmation.'), 800);
      }
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      setTimeout(() => toast.error('Payment cancelled. Your booking was not completed - your time is not held.'), 800);
      let pb = {};
      try { pb = JSON.parse(localStorage.getItem('pendingBooking') || '{}'); } catch { pb = {}; }
      trackFunnel('payment_failed', { package_name: pb.ga_package || 'Recording Session', booking_value: pb.price || 0, reason: 'checkout_cancelled' });
      localStorage.removeItem('pendingBooking');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  // Fetch blocked time slots when engineer or date changes
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedEngineer || !selectedDate) {
        setBlockedSlots([]);
        return;
      }

      try {
        const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
        // Pass selected duration to check for overlaps
        const duration = (selectedDuration || 2) + dealFreeHours(selectedDuration);
        const response = await axios.get(`${API}/bookings/availability/${selectedEngineer}/${dateStr}?duration=${duration}`,
          { headers: localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {} });
        const newBlockedSlots = response.data.blocked_slots || [];
        setBlockedSlots(newBlockedSlots);
        
        // CRITICAL FIX: Clear selected time if it's now blocked for the new engineer/date/duration
        // This prevents users from keeping a blocked time slot selected after switching engineers
        if (selectedTime && newBlockedSlots.includes(selectedTime)) {
          console.log(`Clearing selected time ${selectedTime} - now blocked for this engineer`);
          setSelectedTime(null);
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        setBlockedSlots([]);
      }
    };

    fetchAvailability();
    // Keep availability live: refresh every 60s and whenever the tab regains focus
    const timer = setInterval(fetchAvailability, 60000);
    window.addEventListener('focus', fetchAvailability);
    return () => { clearInterval(timer); window.removeEventListener('focus', fetchAvailability); };
  }, [selectedEngineer, selectedDate, selectedDuration, selectedTime, bonusHoursDeal]);

  // Check PWA discount eligibility when email changes
  useEffect(() => {
    const checkPWADiscount = async () => {
      const hasPWA = localStorage.getItem('pwa_installed') === 'true';
      if (!hasPWA || !formData.email) {
        setPwaDiscountAvailable(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/pwa-discount-status`, {
          params: { email: formData.email }
        });
        setPwaDiscountAvailable(response.data.has_discount);
      } catch (error) {
        console.error('Error checking PWA discount:', error);
        setPwaDiscountAvailable(false);
      }
    };

    checkPWADiscount();
  }, [formData.email]);

  // Validate referral code when it changes
  useEffect(() => {
    const validateReferralCode = async () => {
      if (!formData.referralCode || formData.referralCode.length < 4) {
        setReferralDiscount(null);
        return;
      }

      setIsValidatingReferral(true);
      try {
        const response = await axios.post(`${API}/referrals/validate`, {
          code: formData.referralCode,
          email: formData.email
        });
        
        if (response.data.valid) {
          setReferralDiscount({
            valid: true,
            discount_percentage: response.data.discount_percentage || 20,
            message: response.data.message
          });
          toast.success(`Referral code applied! ${response.data.discount_percentage || 20}% off!`);
        } else {
          setReferralDiscount({ valid: false, message: response.data.message });
        }
      } catch (error) {
        console.error('Error validating referral code:', error);
        setReferralDiscount({ valid: false, message: 'Could not validate code' });
      }
      setIsValidatingReferral(false);
    };

    // Debounce the validation
    const timeoutId = setTimeout(validateReferralCode, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.referralCode, formData.email]);

  // Validate discount code when it changes
  useEffect(() => {
    const validateDiscountCode = async () => {
      if (!discountCode || discountCode.length < 4) {
        setDiscountResult(null);
        return;
      }
      setIsValidatingDiscount(true);
      try {
        const isTrapSpadeDeal = !!(trapSpadeDeal && trapSpadeDeal.applies);
        const response = await axios.post(`${API}/discount/validate`, {
          code: discountCode,
          email: formData.email,
          is_deal_booking: isTrapSpadeDeal
        });
        setDiscountResult(response.data);
        if (response.data.valid) {
          toast.success(`${response.data.discount_percent}% discount applied!`);
        }
      } catch {
        setDiscountResult({ valid: false, message: 'Could not validate code' });
      }
      setIsValidatingDiscount(false);
    };
    const timeoutId = setTimeout(validateDiscountCode, 500);
    return () => clearTimeout(timeoutId);
  }, [discountCode, formData.email, selectedEngineer, selectedDuration]);

  // Check if a time slot is valid based on current time, booking duration, and 2-hour advance requirement
  const isTimeSlotTooSoon = (timeSlot) => {
    if (!selectedDate || !selectedDuration) return false;
    
    const bookingDateTime = new Date(selectedDate);
    const timeParts = timeSlot.match(/(\d+):(\d+)\s*(AM|PM)/i);
    let isLateNightSlot = false;
    
    if (timeParts) {
      let hours = parseInt(timeParts[1]);
      const minutes = parseInt(timeParts[2]);
      const period = timeParts[3].toUpperCase();
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      // Check if this is a late-night slot (12 AM - 5:59 AM)
      isLateNightSlot = (hours >= 0 && hours < 6);
      
      bookingDateTime.setHours(hours, minutes, 0, 0);
      
      // CRITICAL FIX: Late-night slots (12 AM - 5 AM) are actually for the NEXT calendar day
      // If you select Dec 19 and choose 1:00 AM, that's actually Dec 20 at 1:00 AM
      // (the night/late-night of Dec 19)
      if (isLateNightSlot) {
        bookingDateTime.setDate(bookingDateTime.getDate() + 1);
      }
    }
    
    const now = new Date();
    const hoursUntilBooking = (bookingDateTime - now) / (1000 * 60 * 60);
    
    // Simple check: if the booking time is at least 2 hours from now, it's OK
    return hoursUntilBooking < 2;
  };

  // Check if booking is last minute (2-4 hours window = $20 fee)
  const isLastMinuteBooking = useMemo(() => {
    if (!selectedDate || !selectedTime) return false;
    
    // Parse the selected date and time
    const bookingDateTime = new Date(selectedDate);
    const timeParts = selectedTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (timeParts) {
      let hours = parseInt(timeParts[1]);
      const minutes = parseInt(timeParts[2]);
      const period = timeParts[3].toUpperCase();
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      bookingDateTime.setHours(hours, minutes, 0, 0);
      
      // CRITICAL FIX: Late-night slots (12 AM - 5 AM) are actually for the NEXT calendar day
      if (hours >= 0 && hours < 6) {
        bookingDateTime.setDate(bookingDateTime.getDate() + 1);
      }
    }
    
    // Calculate hours until booking
    const now = new Date();
    const hoursUntilBooking = (bookingDateTime - now) / (1000 * 60 * 60);
    
    // Apply $20 fee if booking is in the 2-4 hour window
    return hoursUntilBooking >= 2 && hoursUntilBooking <= 4;
  }, [selectedDate, selectedTime]);

  // Check if Early Bird Special applies (Chapo only, 9am-1pm, M-F)
  // Trap Spade Active Deal (dynamic from DB)
  const [trapSpadePromo, setTrapSpadePromo] = useState(null);
  // General Early Bird Deal (applies to all engineers)
  const [earlyBirdDeal, setEarlyBirdDeal] = useState(null);
  const [fourPlusOneDeal, setFourPlusOneDeal] = useState(null);
  // bonusHoursDeal state lives near the top (used by the availability fetch)
  
  useEffect(() => {
    const checkTrapSpadePromo = async () => {
      try {
        const response = await axios.get(`${API}/trapspade-deal-status`);
        setTrapSpadePromo(response.data);
      } catch (error) {
        console.error('Error checking Trap Spade promo:', error);
      }
    };
    const checkEarlyBird = async () => {
      try {
        const response = await axios.get(`${API}/early-bird-deal`);
        setEarlyBirdDeal(response.data);
      } catch (error) {
        console.error('Error checking early bird deal:', error);
      }
    };
    const checkFourPlusOne = async () => {
      try {
        const response = await axios.get(`${API}/deals/four-plus-one`);
        setFourPlusOneDeal(response.data);
      } catch (error) {
        console.error('Error checking 4+1 deal:', error);
      }
    };
    const checkBonusHours = async () => {
      try {
        const response = await axios.get(`${API}/deals/bonus-hours`);
        setBonusHoursDeal(response.data);
      } catch (error) {
        console.error('Error checking bonus-hours deal:', error);
      }
    };
    checkTrapSpadePromo();
    checkEarlyBird();
    checkFourPlusOne();
    checkBonusHours();
  }, []);

  // 4+1 Free deal: applies when duration = 5 hours and engineer is eligible.
  // Takes priority over standard 5-hour rate but yields to Trap Spade custom deal.
  const fourPlusOneMatch = useMemo(() => {
    if (!fourPlusOneDeal || !fourPlusOneDeal.is_active) return null;
    if (selectedDuration !== (fourPlusOneDeal.required_hours || 5)) return null;
    if (!selectedEngineer) return null;
    const engEntry = (fourPlusOneDeal.engineers || []).find(e => e.id === selectedEngineer);
    if (!engEntry) return null;
    return {
      applies: true,
      dealPrice: engEntry.deal_price,
      regularPrice: engEntry.regular_price,
      savings: engEntry.regular_price - engEntry.deal_price,
      engineerName: engEntry.name,
      dealName: fourPlusOneDeal.deal_name || 'Book 4 Hours, Get 1 FREE',
    };
  }, [fourPlusOneDeal, selectedDuration, selectedEngineer]);

  const trapSpadeDeal = useMemo(() => {
    // Only applies to Trap Spade (engineer ID 1) when deal is active
    if (!selectedEngineer || selectedEngineer !== 1) return null;
    if (!trapSpadePromo || !trapSpadePromo.is_active) return null;
    
    const dealHours = trapSpadePromo.hours || 5;
    // Only applies when the matching duration is selected
    if (selectedDuration !== dealHours) return null;
    
    return {
      applies: true,
      dealPrice: trapSpadePromo.deal_price || 150,
      regularPrice: trapSpadePromo.regular_price || 250,
      savings: trapSpadePromo.savings || 100,
      remainingSlots: trapSpadePromo.remaining_slots,
      isUnlimited: trapSpadePromo.is_unlimited,
      onlyDuration: trapSpadePromo.only_duration || dealHours,
      dealName: trapSpadePromo.deal_name || `Early Bird Special $${trapSpadePromo.deal_price || 150}`,
      expiresAt: trapSpadePromo.expires_at,
      startTime: trapSpadePromo.start_time || '8:00 AM',
      endTime: trapSpadePromo.end_time || '1:00 PM'
    };
  }, [selectedEngineer, selectedDuration, trapSpadePromo]);

  // Determine if Trap Spade has a restricted deal (only specific duration allowed)
  const trapSpadeOnlyDuration = trapSpadePromo?.is_active && trapSpadePromo?.only_duration && selectedEngineer === 1
    ? trapSpadePromo.only_duration : null;

  // Auto-select the only available duration when Trap Spade is selected with restricted deal
  useEffect(() => {
    if (trapSpadeOnlyDuration && selectedEngineer === 1) {
      setSelectedDuration(trapSpadeOnlyDuration);
    }
  }, [selectedEngineer, trapSpadeOnlyDuration]);

  // Force full payment for Trap Spade deal - no deposit option
  useEffect(() => {
    if (trapSpadeDeal && trapSpadeDeal.applies) {
      setPaymentType('full');
    }
  }, [trapSpadeDeal]);

  // Legacy Early Bird Special - now powered by general early bird deal
  const earlyBirdSpecial = useMemo(() => {
    if (!earlyBirdDeal || !earlyBirdDeal.is_active) return null;
    if (selectedDuration !== earlyBirdDeal.hours) return null;
    // Restrict to specific engineers if engineer_ids configured
    const allowedEngineers = earlyBirdDeal.engineer_ids || [];
    if (allowedEngineers.length > 0 && !allowedEngineers.includes(selectedEngineer)) return null;
    // Only applies if the selected time matches the early bird start_time (e.g. 8:00 AM)
    // so the full session fits within start_time–end_time.
    if (selectedTime && earlyBirdDeal.start_time && selectedTime !== earlyBirdDeal.start_time) {
      return null;
    }
    return {
      applies: true,
      dealPrice: earlyBirdDeal.deal_price,
      regularPrice: earlyBirdDeal.regular_price,
      savings: earlyBirdDeal.savings,
      startTime: earlyBirdDeal.start_time,
      endTime: earlyBirdDeal.end_time,
      dealName: earlyBirdDeal.deal_name
    };
  }, [earlyBirdDeal, selectedDuration, selectedTime, selectedEngineer]);

  // Calculate price based on engineer and duration
  const calculatedPrice = useMemo(() => {
    if (!selectedEngineer || !selectedDuration) return null;
    
    const engineer = engineers.find(e => e.id === selectedEngineer);
    if (!engineer) return null;

    // Calculate price with bundle deals
    let price;
    let original;
    let savings = 0;
    let isBundle = false;
    let isEarlyBird = false;
    let isTrapSpadeDeal = false;
    let isFourPlusOne = false;
    
    // Check for active Trap Spade deal (highest priority — custom admin deal)
    if (trapSpadeDeal && trapSpadeDeal.applies) {
      price = trapSpadeDeal.dealPrice;
      original = trapSpadeDeal.regularPrice;
      savings = trapSpadeDeal.savings;
      isTrapSpadeDeal = true;
    }
    // Check for 4+1 Free deal (5-hour session with eligible engineer)
    else if (fourPlusOneMatch && fourPlusOneMatch.applies) {
      price = fourPlusOneMatch.dealPrice;
      original = fourPlusOneMatch.regularPrice;
      savings = fourPlusOneMatch.savings;
      isFourPlusOne = true;
      isBundle = true;
    }
    // Check for Early Bird Special (general deal for all engineers)
    else if (earlyBirdSpecial && earlyBirdSpecial.applies) {
      price = earlyBirdSpecial.dealPrice;
      original = earlyBirdSpecial.regularPrice;
      savings = earlyBirdSpecial.savings;
      isEarlyBird = true;
      original = engineer.hourlyRate * selectedDuration;
      savings = original - price;
      isEarlyBird = true;
    }
    else if (selectedDuration === 8 && engineer.eightHourRate) {
      price = engineer.eightHourRate;
      original = engineer.hourlyRate * 8;
      savings = original - price;
      isBundle = savings > 0;
      if (!isBundle) { original = null; savings = 0; }
    }
    // Check for special rates (Trap Spade only)
    else if (selectedDuration === 5 && engineer.fiveHourRate) {
      // Special 5-hour rate for Trap Spade
      price = engineer.fiveHourRate;
      original = engineer.hourlyRate * 5;
      savings = original - price;
      isBundle = true;
    } else if (selectedDuration === 6 && engineer.sixHourRate) {
      // Special 6-hour rate for Trap Spade
      price = engineer.sixHourRate;
      original = engineer.hourlyRate * 6;
      savings = original - price;
      isBundle = true;
    } else if (selectedDuration <= 2) {
      // Regular hourly rate for 1-2 hours
      price = engineer.hourlyRate * selectedDuration;
      original = null;
    } else if (selectedDuration === 3) {
      // Exactly 3 hours - single bundle
      price = engineer.bundleRate;
      original = engineer.hourlyRate * 3;
      savings = original - price;
      isBundle = true;
    } else if (selectedDuration === 6) {
      // 6 hours = 2 bundles (for engineers without special rate)
      price = engineer.bundleRate * 2;
      original = engineer.hourlyRate * 6;
      savings = original - price;
      isBundle = true;
    } else {
      // For 4+ hours (not 5 or 6), use mixed bundle pricing
      const bundles = Math.floor(selectedDuration / 3);
      const remainingHours = selectedDuration % 3;
      price = (bundles * engineer.bundleRate) + (remainingHours * engineer.hourlyRate);
      original = engineer.hourlyRate * selectedDuration;
      savings = original - price;
      isBundle = bundles > 0;
    }

    // Apply promotional discount if available (NOT on top of Trap Spade deal)
    let promoDiscount = 0;
    let priceBeforePromo = price;
    if (promoStatus && promoStatus.is_active && !isTrapSpadeDeal) {
      promoDiscount = price * (promoStatus.discount_percentage / 100);
      price = price - promoDiscount;
    }

    // Apply PWA discount only on first booking through the app (NOT on top of Trap Spade deal)
    let pwaDiscount = 0;
    if (pwaDiscountAvailable && !isTrapSpadeDeal) {
      pwaDiscount = price * 0.10; // 10% discount for first PWA booking
      price = price - pwaDiscount;
    }

    // Apply referral code discount (20% off) - NOT on Trap Spade deal
    let referralDiscountAmount = 0;
    if (referralDiscount && referralDiscount.valid && !promoDiscount && !pwaDiscount && !isTrapSpadeDeal) {
      referralDiscountAmount = price * (referralDiscount.discount_percentage / 100);
      price = price - referralDiscountAmount;
    }

    // Apply discount code (WEBACK10, RELINK-xxx) - NOT on Trap Spade deal, not combined with other discounts
    let discountCodeAmount = 0;
    if (discountResult && discountResult.valid && !isTrapSpadeDeal && !promoDiscount && !pwaDiscount && !referralDiscountAmount) {
      discountCodeAmount = price * (discountResult.discount_percent / 100);
      price = price - discountCodeAmount;
    }

    // Calculate add-ons total (respects per-addon quantity when quantifiable)
    let addonsTotal = 0;
    selectedAddons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) {
        const qty = addon.quantifiable ? (addonQuantities[addonId] || 1) : 1;
        addonsTotal += addon.price * qty;
      }
    });

    // Add last-minute booking fee if applicable (2-4 hours before session)
    const lastMinuteFee = isLastMinuteBooking ? 20 : 0;
    const finalPrice = price + lastMinuteFee + addonsTotal;

    return {
      price: finalPrice,
      basePrice: price,
      priceBeforePromo,
      original,
      savings,
      isBundle,
      isEarlyBird,
      isTrapSpadeDeal,
      isFourPlusOne,
      lastMinuteFee,
      promoDiscount,
      pwaDiscount,
      referralDiscountAmount,
      discountCodeAmount,
      hasPWA: pwaDiscountAvailable,
      hasPromo: promoStatus && promoStatus.is_active,
      hasReferral: referralDiscount && referralDiscount.valid,
      hasDiscountCode: discountResult && discountResult.valid,
      hasTrapSpadeDeal: trapSpadeDeal && trapSpadeDeal.applies,
      trapSpadeDealRemaining: trapSpadeDeal?.remainingSlots,
      addonsTotal
    };
  }, [selectedEngineer, selectedDuration, isLastMinuteBooking, promoStatus, selectedAddons, addonQuantities, pwaDiscountAvailable, earlyBirdSpecial, referralDiscount, trapSpadeDeal, discountResult]);

  // Analytics (no PII): shared payload + funnel state for abandonment tracking
  const sectionRef = useRef(null);
  const funnelRef = useRef({ step: 'viewed', leaving: false, done: false });
  const gaPayload = () => {
    const eng = engineers.find(e => e.id === selectedEngineer);
    return {
      package_name: eng && selectedDuration ? `${eng.name} - ${selectedDuration} Hours` : (eng ? eng.name : 'Not selected'),
      booking_value: calculatedPrice ? Number(calculatedPrice.price.toFixed(2)) : 0,
    };
  };
  const gaRef = useRef(gaPayload);
  gaRef.current = gaPayload;
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return undefined;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { trackFunnel('view_item', gaRef.current(), 'view_item'); io.disconnect(); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const onLeave = () => {
      const f = funnelRef.current;
      if (f.leaving || f.done || f.step === 'viewed') return;
      trackFunnel('booking_abandoned', { ...gaRef.current(), last_step: f.step }, 'booking_abandoned');
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, []);
  useEffect(() => {
    if (!selectedEngineer) return;
    sessionStorage.setItem('hq_booking_active', '1');
    if (funnelRef.current.step === 'viewed') funnelRef.current.step = 'engineer_selected';
  }, [selectedEngineer]);
  useEffect(() => {
    if (!selectedEngineer || !selectedDuration) return;
    funnelRef.current.step = 'package_selected';
    trackFunnel('select_item', gaRef.current());
  }, [selectedEngineer, selectedDuration]);
  const pickDate = (date) => {
    if (!date) return;
    setSelectedDate(date);
    funnelRef.current.step = 'date_selected';
    trackFunnel('booking_date_selected', { ...gaRef.current(), days_ahead: Math.round((date - new Date(new Date().setHours(0, 0, 0, 0))) / 86400000) });
  };
  const pickTime = (time) => {
    setSelectedTime(time);
    funnelRef.current.step = 'time_selected';
    trackFunnel('booking_time_selected', { ...gaRef.current(), time_slot: time });
  };
  const depositAmountFor = (total) => (paymentType === 'full' ? total : depositFor(total));

  const durations = useMemo(() => {
    const isEbEngineer = !!(earlyBirdDeal?.is_active && earlyBirdDeal?.engineer_ids?.includes(selectedEngineer));
    const ebHours = earlyBirdDeal?.hours || 6;
    // Check eligibility for 4+1 Free deal without triggering it via selectedDuration
    const fourPlusOneEligible = (() => {
      if (!fourPlusOneDeal || !fourPlusOneDeal.is_active) return null;
      const engEntry = (fourPlusOneDeal.engineers || []).find(e => e.id === selectedEngineer);
      if (!engEntry) return null;
      return { dealPrice: engEntry.deal_price, regularPrice: engEntry.regular_price };
    })();
    const bonusTier = (bonusHoursDeal?.tiers && bonusHoursDeal.tiers[0]) || null;
    const bonusMinHours = bonusTier ? Number(bonusTier.min_hours) : 5;
    const bonusEligible = !!(bonusHoursDeal && bonusHoursDeal.is_active);
    const reelFor = (h) => bonusEligible && h >= bonusMinHours;
    const qualifies = (h) => reelFor(h) || dealFreeHours(h) > 0;
    const bonusNote = (h) => {
      const free = dealFreeHours(h);
      if (reelFor(h)) return free ? `+ FREE Reel + ${free} FREE hr (${h + free} hrs total)` : `+ ${bonusTier?.bonus || '1 FREE Reel'}`;
      return free ? `+ ${free} FREE hr Mon-Wed (${h + free} hrs total)` : '';
    };
    const bonusBadge = (h) => (qualifies(h) ? (dealFreeHours(h) ? `+${dealFreeHours(h)} FREE HR` : 'FREE Reel') : '');
    const allDurations = [
      { hours: 2, label: '2 Hours', note: 'Minimum' },
      { hours: 3, label: '3 Hours', note: bonusNote(3) || 'Bundle Deal', highlight: true, badgeText: bonusBadge(3) || 'Most Popular' },
      { hours: 4, label: '4 Hours', note: bonusNote(4), highlight: qualifies(4), badgeText: bonusBadge(4) },
      {
        hours: 5,
        label: '5 Hours',
        note: fourPlusOneEligible ? '4 hrs + 1 FREE' : bonusNote(5),
        highlight: !!fourPlusOneEligible || qualifies(5),
        badgeText: fourPlusOneEligible ? `$${fourPlusOneEligible.dealPrice} Deal` : bonusBadge(5)
      },
      {
        hours: 6,
        label: '6 Hours',
        note: bonusNote(6) || (isEbEngineer ? `Early Bird ${earlyBirdDeal.start_time}-${earlyBirdDeal.end_time} only` : ''),
        highlight: qualifies(6) || (isEbEngineer && ebHours === 6),
        badgeText: bonusBadge(6) || ((isEbEngineer && ebHours === 6) ? `$${earlyBirdDeal.deal_price} Deal` : '')
      }
    ];
    const selEng = engineers.find(e => e.id === selectedEngineer);
    if (!selectedEngineer || selEng?.eightHourRate) {
      allDurations.push({
        hours: 8,
        label: '8 Hours',
        note: bonusNote(8) || 'Full Day',
        highlight: qualifies(8),
        badgeText: bonusBadge(8) || 'Full Day'
      });
    }

    // If Trap Spade has a restricted deal, only show that duration
    if (trapSpadeOnlyDuration && selectedEngineer === 1) {
      const dealDuration = allDurations.find(d => d.hours === trapSpadeOnlyDuration);
      if (dealDuration) {
        const timeRange = trapSpadePromo.start_time && trapSpadePromo.end_time
          ? ` (${trapSpadePromo.start_time} - ${trapSpadePromo.end_time})`
          : '';
        return [{
          ...dealDuration,
          highlight: true,
          badgeText: `$${trapSpadePromo.deal_price} Special`,
          note: (trapSpadePromo.deal_name || `${trapSpadeOnlyDuration} Hours for $${trapSpadePromo.deal_price}`) + timeRange
        }];
      }
    }

    return allDurations;
  }, [selectedEngineer, selectedDate, trapSpadeOnlyDuration, trapSpadePromo, earlyBirdDeal, fourPlusOneDeal, bonusHoursDeal]);

  // Package deals
  const packageDeals = [
    {
      id: 'pkg1',
      name: '3 Hours + 1 Video',
      hours: 3,
      includes: ['3 Hours Studio Time', '1 Video Production'],
      price: 300,
      highlight: false
    },
    {
      id: 'pkg2',
      name: '3 Hours + Beats or Mix',
      hours: 3,
      includes: ['3 Hours Studio Time', '3 Beat Leases OR 1 Mix & Master'],
      price: 200,
      highlight: true,
      badgeText: 'Best Value'
    },
    {
      id: 'pkg3',
      name: '6 Hours Premium Package',
      hours: 6,
      includes: ['6 Hours Studio Time', '1 Video Production', '3 Beat Leases OR 1 Mix & Master'],
      price: 550,
      highlight: false
    }
  ];

  const handlePackageBooking = async (e) => {
    e.preventDefault();

    if (!packageFormData.name || !packageFormData.email || !packageFormData.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!packageFormData.requestedDate || !packageFormData.requestedTime) {
      toast.error("Please select your preferred date and time.");
      return;
    }

    setIsPackageSubmitting(true);

    try {
      const depositAmount = 100; // $100 deposit for package deals
      
      // Track bundle inquiry
      trackBundleInquiry(selectedPackage.name, selectedPackage.price);
      
      const bookingData = {
        name: packageFormData.name,
        email: packageFormData.email,
        phone: packageFormData.phone,
        engineer_id: 1, // Default to Trap Spade for packages
        engineer_name: "Package Deal",
        service_id: 2, // Package service
        service_name: `${selectedPackage.name} - $${selectedPackage.price}`,
        date: packageFormData.requestedDate,
        time: packageFormData.requestedTime,
        notes: `PACKAGE DEAL INQUIRY: ${selectedPackage.name}\n\nIncludes: ${selectedPackage.includes.join(', ')}\n\nCustomer Notes: ${packageFormData.notes}`,
        price: selectedPackage.price
      };

      // Create Stripe checkout session for package deposit
      const checkoutData = {
        product_name: `Package Deposit: ${selectedPackage.name}`,
        product_description: `$100 deposit for ${selectedPackage.name} on ${packageFormData.requestedDate} at ${packageFormData.requestedTime}. We'll contact you to confirm availability.`,
        unit_amount: depositAmount * 100, // Convert to cents
        quantity: 1,
        success_url: `${window.location.origin}/?payment=success`,
        cancel_url: `${window.location.origin}/?payment=cancelled`,
        metadata: {
          booking_type: 'package_deal',
          booking_data: JSON.stringify(bookingData)
        }
      };

      const checkoutResponse = await axios.post(`${API}/create-checkout-session`, checkoutData);
      
      if (checkoutResponse.data && checkoutResponse.data.url) {
        // Store booking data in localStorage to complete after payment
        localStorage.setItem('pendingBooking', JSON.stringify(bookingData));
        
        // Redirect to Stripe checkout
        window.location.href = checkoutResponse.data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Package booking error:', error);
      toast.error(error.response?.data?.detail || "There was an error processing your request. Please try again.");
    } finally {
      setIsPackageSubmitting(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();

    // Validate form fields
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields (name, email, phone).");
      return;
    }

    if (!selectedEngineer || !selectedDuration || !selectedTime || !selectedDate) {
      toast.error("Please select an engineer, duration, date, and time slot.");
      return;
    }

    // Check if booking is too soon (within 2 hours)
    if (isTimeSlotTooSoon(selectedTime)) {
      toast.error("Bookings must be made at least 2 hours in advance.");
      setSelectedTime(null);
      return;
    }

    // CRITICAL: Final validation - ensure selected time is not blocked
    // This catches edge cases where user may have kept a blocked slot selected
    if (blockedSlots.includes(selectedTime)) {
      toast.error("This time slot is no longer available. Please select a different time.");
      setSelectedTime(null);
      return;
    }

    setIsSubmitting(true);
    funnelRef.current.step = 'checkout_started';
    trackFunnel('begin_checkout', { ...gaPayload(), payment_type: paymentType });

    try {
      const engineer = engineers.find(e => e.id === selectedEngineer);
      const price = calculatedPrice.price;
      
      // Adjust deposit based on payment type and credits usage
      let depositAmount = depositAmountFor(price); // Full payment or fixed $40 deposit
      
      // Force full payment for Trap Spade deal
      if (calculatedPrice.hasTrapSpadeDeal) {
        depositAmount = price;
      }
      
      if (appliedCredits && appliedCredits.hoursToPay === 0) {
        // If all hours are covered by credits, no deposit needed
        depositAmount = 0;
      } else if (appliedCredits && appliedCredits.hoursToPay > 0 && appliedCredits.hoursToPay < selectedDuration) {
        // Partial credits applied - use the reduced final price
        const creditAdjustedPrice = appliedCredits.finalPrice;
        depositAmount = depositAmountFor(creditAdjustedPrice);
      }
      
      // Check if booking is through PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                           window.navigator.standalone || 
                           document.referrer.includes('android-app://');
      
      // Track booking start
      trackBookingStart(engineer.name, `${selectedDuration}hr Session`);
      
      // Track PWA booking if applicable
      if (isStandalone) {
        trackPWABooking(engineer.name, `${selectedDuration}hr Session`, price, calculatedPrice.pwaDiscount > 0);
        
        // Track PWA discount if applied
        if (calculatedPrice.pwaDiscount > 0) {
          trackPWADiscountApplied(calculatedPrice.pwaDiscount, price);
        }
      }
      
      // Format date properly - always use selected date, format as YYYY-MM-DD
      // CRITICAL FIX: Late-night slots (12 AM - 5 AM) are actually for the NEXT calendar day
      let bookingDate = new Date(selectedDate);
      
      // Check if selected time is a late-night slot (12 AM - 5 AM)
      const timeParts = selectedTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (timeParts) {
        let hours = parseInt(timeParts[1]);
        const period = timeParts[3].toUpperCase();
        
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        
        // If time is between 12 AM (0) and 5 AM, it's actually for the next day
        if (hours >= 0 && hours < 6) {
          bookingDate.setDate(bookingDate.getDate() + 1);
        }
      }
      
      const formattedDate = bookingDate 
        ? `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}-${String(bookingDate.getDate()).padStart(2, '0')}`
        : null;
      
      if (!formattedDate) {
        toast.error('Please select a date');
        return;
      }
      
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        engineer_id: selectedEngineer,
        engineer_name: engineer.name,
        service_id: 1, // Recording service
        service_name: `Recording Session - ${selectedDuration} Hours - $${price.toFixed(2)}${selectedAddons.length > 0 ? ' + ' + selectedAddons.map(id => {
          const a = addons.find(x => x.id === id);
          if (!a) return null;
          const q = a.quantifiable ? (addonQuantities[id] || 1) : 1;
          return q > 1 ? `${a.name} × ${q}` : a.name;
        }).filter(Boolean).join(', ') : ''}`,
        date: formattedDate,
        time: selectedTime,
        notes: formData.notes,
        price: appliedCredits ? appliedCredits.finalPrice : price,
        original_price: price,
        referral_code: referralDiscount?.valid ? formData.referralCode : null,
        discount_code: discountResult?.valid ? discountCode : null,
        discount_percent: discountResult?.valid ? discountResult.discount_percent : 0,
        credits_used: appliedCredits ? appliedCredits.creditsUsed : 0,
        hours_covered_by_credits: appliedCredits ? appliedCredits.hoursCovered : 0,
        credit_discount: appliedCredits ? appliedCredits.discountAmount : 0,
        addons: selectedAddons.map(addonId => {
          const addon = addons.find(a => a.id === addonId);
          if (!addon) return null;
          const qty = addon.quantifiable ? (addonQuantities[addonId] || 1) : 1;
          return {
            id: addon.id,
            name: qty > 1 ? `${addon.name} × ${qty}` : addon.name,
            price: addon.price * qty,
            unit_price: addon.price,
            quantity: qty,
          };
        }).filter(Boolean),
        sms_consent: agreedToSms === true,
        sms_consent_timestamp: agreedToSms ? new Date().toISOString() : null
      };

      if (usePlanHours && planUsable) {
        const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/book`, { booking: bookingData },
          { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` } });
        toast.success(`Booked with your Artist Plan. ${r.data.hours_left} plan hours left.`);
        funnelRef.current.done = true;
        trackPurchaseOnce(r.data.booking_id || r.data.booking?.id || `plan-${Date.now()}`, { ...gaPayload(), value: 0, payment_type: 'artist_plan' });
        setArtistPlan({ ...artistPlan, hours_left: r.data.hours_left });
        setUsePlanHours(false);
        setSelectedDuration(null);
        setSelectedTime(null);
        return;
      }

      // If booking is completely covered by credits (FREE), create booking directly
      if (depositAmount === 0 && appliedCredits) {
        const createResponse = await axios.post(`${API}/bookings`, bookingData, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` } });
        
        if (createResponse.data) {
          // Redeem credits after successful booking
          await axios.post(`${API}/credits/redeem`, {
            email: formData.email,
            credits_to_redeem: appliedCredits.creditsUsed,
            booking_id: createResponse.data.id
          }, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` } });
          
          toast.success('🎉 FREE Booking Confirmed with Credits! Check your email for confirmation.');
          funnelRef.current.done = true;
          trackPurchaseOnce(createResponse.data.id, { ...gaPayload(), value: 0, payment_type: 'credits' });
          
          // Reset form
          setFormData({ name: '', email: '', phone: '', notes: '', referralCode: '', smsConsent: false });
          setSelectedEngineer(null);
          setSelectedDuration(null);
          setSelectedTime(null);
          setSelectedDate(new Date(new Date().setHours(0, 0, 0, 0)));
          setAppliedCredits(null);
          setDiscountCode('');
          setDiscountResult(null);
          return;
        } else {
          throw new Error('Failed to create booking');
        }
      }

      // Create Stripe checkout session for payment with booking metadata
      const creditNote = appliedCredits ? ` (${appliedCredits.hoursCovered}hr covered by credits)` : '';
      const checkoutData = {
        product_name: `${paymentType === 'full' ? 'Full Payment' : 'Deposit'}: ${engineer.name} - ${selectedDuration}hr Session${creditNote}`,
        product_description: `${paymentType === 'full' ? 'Full payment' : 'Deposit'} for session on ${selectedDate.toLocaleDateString()} at ${selectedTime}.${creditNote} ${paymentType === 'deposit' ? 'Remaining balance due at the studio on session day.' : 'No balance due.'}`,
        unit_amount: Math.round(depositAmount * 100), // Convert to cents
        quantity: 1,
        success_url: `${window.location.origin}/?payment=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/?payment=cancelled`,
        metadata: {
          booking_type: 'recording_session',
          payment_type: paymentType,
          booking_data: JSON.stringify(bookingData)
        }
      };

      const checkoutResponse = await axios.post(`${API}/create-checkout-session`, checkoutData);
      
      if (checkoutResponse.data && checkoutResponse.data.url) {
        // Store booking data in localStorage to complete after payment
        const ga = gaPayload();
        localStorage.setItem('pendingBooking', JSON.stringify({
          ...bookingData,
          ga_txn: checkoutResponse.data.session_id || `bk-${Date.now()}`,
          ga_package: ga.package_name,
          ga_amount_paid: depositAmount,
          ga_payment_type: paymentType,
        }));
        funnelRef.current.leaving = true;
        trackFunnel('add_payment_info', { ...ga, payment_type: paymentType, amount_due_now: depositAmount });
        
        // Store discount code to mark as used after payment
        if (discountResult?.valid && discountCode) {
          localStorage.setItem('pendingDiscountCode', JSON.stringify({
            code: discountCode,
            email: formData.email
          }));
        }
        
        // Redirect to Stripe checkout
        window.location.href = checkoutResponse.data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }

      // Refresh promo status
      try {
        const promoResponse = await axios.get(`${API}/promo-status`);
        setPromoStatus(promoResponse.data);
      } catch (error) {
        console.error('Error refreshing promo status:', error);
      }

      // Reset form
      setFormData({ name: '', email: '', phone: '', notes: '', smsConsent: false });
      setSelectedEngineer(null);
      setSelectedDuration(null);
      setSelectedTime(null);
      setSelectedDate(new Date(new Date().setHours(0, 0, 0, 0)));
    } catch (error) {
      console.error('Booking error:', error);
      trackFunnel('payment_failed', { ...gaPayload(), reason: 'checkout_error' });
      toast.error(error.response?.data?.detail || "There was an error processing your booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" ref={sectionRef} className="py-20 bg-gradient-to-b from-purple-950/20 to-black">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          {!hideHeader && (
            <>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Book Your <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Session</span>
              </h2>
              <p className="text-gray-400 text-lg">24/7 availability - Choose your engineer and duration, price auto-calculates</p>
              <p className="text-purple-400 text-sm mt-2">💰 3-Hour Bundle Deal saves you money!</p>
            </>
          )}

          
          {/* Promotional Banner */}
          {promoStatus && promoStatus.is_active && (
            <div className="mt-6 max-w-2xl mx-auto">
              <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-sm border border-purple-400/30 rounded-lg px-6 py-4 shadow-lg">
                <div className="flex items-center justify-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center border border-purple-400/50">
                      <span className="text-xl">✨</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold text-base">
                      {promoStatus.promo_name || 'Limited Time Offer'}: {promoStatus.discount_percentage}% Off Your Session
                    </p>
                    <p className="text-purple-300 text-sm mt-1">
                      {promoStatus.remaining_slots} {promoStatus.remaining_slots === 1 ? 'spot' : 'spots'} remaining!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Selection */}
            <div className="space-y-6">
              {/* Engineer Selection */}
              <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <User className="text-purple-400" size={20} />
                  <h3 className="text-xl font-bold text-white">Select Engineer</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {engineers.filter(e => e.active !== false).map((engineer) => (
                    <button
                      key={engineer.id}
                      data-testid={`engineer-select-${engineer.id}`}
                      onClick={() => setSelectedEngineer(engineer.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedEngineer === engineer.id
                          ? 'border-purple-500 bg-purple-600/20'
                          : 'border-purple-500/20 hover:border-purple-500/50 bg-black/20'
                      }`}
                    >
                      <div className="font-semibold text-white text-sm mb-1">{engineer.name}</div>
                      <div className="text-xs text-gray-400">{engineer.specialty}</div>
                      <div data-testid={`engineer-rate-${engineer.id}`} className="text-xs text-purple-300 font-semibold mt-1">${engineer.hourlyRate}/hr</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration Selection */}
              <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Clock className="text-purple-400" size={20} />
                  <h3 className="text-xl font-bold text-white">Session Duration</h3>
                </div>
                <p data-testid="duration-rules-note" className="text-xs text-gray-400 mb-4 -mt-2">
                  Hourly recording sessions · {MIN_SESSION_HOURS}-hour minimum{selectedEngineer ? ` · $${engineers.find(e => e.id === selectedEngineer)?.hourlyRate}/hr` : ''}. Badged options are limited-time promotions.
                </p>
                <div className="space-y-3">
                  {durations.map((duration) => {
                    const engineer = selectedEngineer && engineers.find(e => e.id === selectedEngineer);
                    let priceDisplay = null;
                    
                    if (engineer) {
                      // Check if this is Trap Spade's active deal duration
                      if (trapSpadeOnlyDuration && selectedEngineer === 1 && duration.hours === trapSpadeOnlyDuration) {
                        const dealPrice = trapSpadePromo.deal_price || 100;
                        const regularPrice = trapSpadePromo.regular_price || engineer.bundleRate;
                        priceDisplay = (
                          <div className="text-right">
                            <div className="text-xs text-yellow-400 mb-1 font-bold animate-pulse" data-testid="deal-badge">{trapSpadePromo?.deal_type === 'early_bird_special' ? 'EARLY BIRD SPECIAL' : 'SPECIAL DEAL'}</div>
                            <div className="text-lg font-bold text-yellow-400" data-testid="deal-price">
                              ${dealPrice}
                            </div>
                            <div className="text-xs text-gray-500 line-through">
                              ${regularPrice}
                            </div>
                          </div>
                        );
                      } else if (duration.hours === 8 && engineer.eightHourRate) {
                        priceDisplay = (
                          <div className="text-right" data-testid="eight-hour-price">
                            <div className="text-xs text-gray-400 mb-1">Full Day</div>
                            <div className="text-lg font-bold text-green-400">${engineer.eightHourRate}</div>
                            {engineer.hourlyRate * 8 > engineer.eightHourRate && <div className="text-xs text-gray-500 line-through">${engineer.hourlyRate * 8}</div>}
                          </div>
                        );
                      } else if (duration.hours === 3) {
                        priceDisplay = (
                          <div className="text-right">
                            <div className="text-xs text-gray-400 mb-1">Bundle Deal</div>
                            <div className="text-lg font-bold text-green-400">
                              ${engineer.bundleRate}
                            </div>
                            {(engineer.hourlyRate * 3) > (engineer.bundleRate) && (
                              <div className="text-xs text-gray-500 line-through">${engineer.hourlyRate * 3}</div>
                            )}
                          </div>
                        );
                      } else if (duration.hours === 5 && earlyBirdDeal?.is_active && earlyBirdDeal?.hours === 5 && earlyBirdDeal?.engineer_ids?.includes(selectedEngineer)) {
                        priceDisplay = (
                          <div className="text-right">
                            <div className="text-xs text-yellow-400 mb-1 font-bold animate-pulse" data-testid="early-bird-badge">EARLY BIRD SPECIAL</div>
                            <div className="text-lg font-bold text-yellow-400" data-testid="early-bird-price">
                              ${earlyBirdDeal.deal_price}
                            </div>
                            <div className="text-xs text-gray-500 line-through">
                              ${earlyBirdDeal.regular_price}
                            </div>
                            <div className="text-[10px] text-purple-400">
                              {earlyBirdDeal.start_time} - {earlyBirdDeal.end_time}
                            </div>
                          </div>
                        );
                      } else if (duration.hours === 6 && earlyBirdDeal?.is_active && earlyBirdDeal?.hours === 6 && earlyBirdDeal?.engineer_ids?.includes(selectedEngineer)) {
                        priceDisplay = (
                          <div className="text-right">
                            <div className="text-xs text-yellow-400 mb-1 font-bold animate-pulse" data-testid="early-bird-badge">EARLY BIRD SPECIAL</div>
                            <div className="text-lg font-bold text-yellow-400" data-testid="early-bird-price">
                              ${earlyBirdDeal.deal_price}
                            </div>
                            <div className="text-xs text-gray-500 line-through">
                              ${earlyBirdDeal.regular_price}
                            </div>
                            <div className="text-[10px] text-purple-400">
                              {earlyBirdDeal.start_time} only
                            </div>
                            <div className="text-[10px] text-gray-400 mt-1">
                              Other times: ${engineer.bundleRate * 2}
                            </div>
                          </div>
                        );
                      } else if (duration.hours === 6) {
                        if (engineer.sixHourRate) {
                          // Other engineers with 6-hour rate
                          const price = engineer.sixHourRate;
                          const savings = (engineer.hourlyRate * 6) - price;
                          priceDisplay = (
                            <div className="text-right">
                              <div className="text-xs text-gray-400 mb-1">Special Rate</div>
                              <div className="text-lg font-bold text-green-400">
                                ${price}
                              </div>
                              {(engineer.hourlyRate * 6) > (price) && (
                                <div className="text-xs text-gray-500 line-through">${engineer.hourlyRate * 6}</div>
                              )}
                            </div>
                          );
                        } else {
                          // 6 hours = special package for other engineers
                          priceDisplay = (
                            <div className="text-right">
                              <div className="text-xs text-gray-400 mb-1">6-Hour Package</div>
                              <div className="text-lg font-bold text-green-400">
                                ${engineer.bundleRate * 2}
                              </div>
                              {(engineer.hourlyRate * 6) > (engineer.bundleRate * 2) && (
                                <div className="text-xs text-gray-500 line-through">${engineer.hourlyRate * 6}</div>
                              )}
                            </div>
                          );
                        }
                      } else if (duration.hours === 4) {
                        // 4 hours = 1 bundle + 1 hour
                        const price = engineer.bundleRate + engineer.hourlyRate;
                        const savings = (engineer.hourlyRate * 4) - price;
                        priceDisplay = (
                          <div className="text-right">
                            <div className="text-xs text-gray-400 mb-1">Bundle + 1hr</div>
                            <div className="text-lg font-bold text-green-400">
                              ${price}
                            </div>
                            {(engineer.hourlyRate * 4) > (price) && (
                              <div className="text-xs text-gray-500 line-through">${engineer.hourlyRate * 4}</div>
                            )}
                          </div>
                        );
                      } else if (duration.hours === 5) {
                        // Check for special 5-hour rate (Trap Spade only)
                        if (engineer.fiveHourRate) {
                          const price = engineer.fiveHourRate;
                          const savings = (engineer.hourlyRate * 5) - price;
                          priceDisplay = (
                            <div className="text-right">
                              <div className="text-xs text-gray-400 mb-1">Special Rate</div>
                              <div className="text-lg font-bold text-green-400">
                                ${price}
                              </div>
                              {(engineer.hourlyRate * 5) > (price) && (
                                <div className="text-xs text-gray-500 line-through">${engineer.hourlyRate * 5}</div>
                              )}
                            </div>
                          );
                        } else {
                          // 5 hours = 1 bundle + 2 hours for other engineers
                          const price = engineer.bundleRate + (engineer.hourlyRate * 2);
                          const savings = (engineer.hourlyRate * 5) - price;
                          priceDisplay = (
                            <div className="text-right">
                              <div className="text-xs text-gray-400 mb-1">Bundle + 2hrs</div>
                              <div className="text-lg font-bold text-green-400">
                                ${price}
                              </div>
                              {(engineer.hourlyRate * 5) > price && (
                                <div className="text-xs text-gray-500 line-through">${engineer.hourlyRate * 5}</div>
                              )}
                            </div>
                          );
                        }
                      } else {
                        priceDisplay = (
                          <div className="text-right">
                            <div className="text-xs text-gray-400 mb-1">Regular Rate</div>
                            <div className="text-lg font-bold text-purple-400">
                              ${engineer.hourlyRate * duration.hours}
                            </div>
                          </div>
                        );
                      }
                    }
                    
                    return (
                      <button
                        key={duration.hours}
                        data-testid={`duration-option-${duration.hours}`}
                        onClick={() => setSelectedDuration(duration.hours)}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left relative ${
                          selectedDuration === duration.hours
                            ? 'border-purple-500 bg-purple-600/20'
                            : 'border-purple-500/20 hover:border-purple-500/50 bg-black/20'
                        } ${duration.highlight ? 'ring-2 ring-purple-500/30' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-white mb-1 flex items-center space-x-2">
                              <span>{duration.label}</span>
                              {duration.highlight && (
                                <span className={`text-xs text-white px-2 py-0.5 rounded-full flex items-center space-x-1 ${
                                  duration.badgeText === 'Best Deal' 
                                    ? 'bg-gradient-to-r from-green-600 to-emerald-600' 
                                    : 'bg-gradient-to-r from-purple-600 to-blue-600'
                                }`}>
                                  <Zap size={10} />
                                  <span>{duration.badgeText || 'Most Popular'}</span>
                                </span>
                              )}
                            </div>
                            {duration.note && (
                              <div className="text-xs text-gray-400">{duration.note}</div>
                            )}
                          </div>
                          {priceDisplay}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Add-On Services */}
              {selectedEngineer && selectedDuration && (
                <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Add-On Services (Optional)</h3>
                  <div className="space-y-3">
                    {addons.map((addon) => {
                      const isSelected = selectedAddons.includes(addon.id);
                      const qty = addon.quantifiable ? (addonQuantities[addon.id] || 1) : 1;
                      const displayPrice = addon.quantifiable ? addon.price * qty : addon.price;
                      const maxQty = addon.maxQty || 10;
                      
                      return (
                        <div key={addon.id} className="space-y-2">
                          <button
                            onClick={() => {
                              if (isSelected) {
                                setSelectedAddons(selectedAddons.filter(id => id !== addon.id));
                                if (addon.quantifiable) {
                                  const nextQ = { ...addonQuantities };
                                  delete nextQ[addon.id];
                                  setAddonQuantities(nextQ);
                                }
                              } else {
                                setSelectedAddons([...selectedAddons, addon.id]);
                                if (addon.quantifiable) {
                                  setAddonQuantities({ ...addonQuantities, [addon.id]: 1 });
                                }
                              }
                            }}
                            data-testid={`addon-toggle-${addon.id}`}
                            className={`w-full ${addon.compact ? 'px-3 py-2 border' : 'p-4 border-2'} rounded-lg transition-all text-left ${
                              isSelected
                                ? 'border-blue-500 bg-blue-600/20'
                                : 'border-purple-500/20 hover:border-blue-500/50 bg-black/20'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-500'
                                }`}>
                                  {isSelected && <span className="text-white text-xs">✓</span>}
                                </div>
                                <div>
                                  <div className={`font-semibold text-white flex items-center gap-2 ${addon.compact ? 'text-sm' : ''}`}>
                                    {addon.name}
                                    {addon.hasInfo && (
                                      <span className="text-blue-400 text-xs bg-blue-500/20 px-2 py-0.5 rounded-full">ℹ️ Info below</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-400">{addon.description}</div>
                                </div>
                              </div>
                              <div className={`${addon.compact ? 'text-sm' : 'text-lg'} font-bold text-blue-400`}>
                                +${displayPrice}
                              </div>
                            </div>
                          </button>

                          {/* Quantity picker — only for quantifiable addons that are selected */}
                          {isSelected && addon.quantifiable && (
                            <div className="ml-8 flex items-center gap-3 bg-fuchsia-900/20 border border-fuchsia-500/30 rounded-lg p-3">
                              <span className="text-sm text-fuchsia-200 font-semibold">Quantity:</span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newQty = Math.max(1, qty - 1);
                                    setAddonQuantities({ ...addonQuantities, [addon.id]: newQty });
                                  }}
                                  disabled={qty <= 1}
                                  data-testid={`addon-qty-minus-${addon.id}`}
                                  className="w-8 h-8 rounded-full bg-fuchsia-600/40 hover:bg-fuchsia-600 text-white font-bold text-lg disabled:opacity-30 disabled:cursor-not-allowed transition"
                                >
                                  −
                                </button>
                                <span
                                  data-testid={`addon-qty-value-${addon.id}`}
                                  className="text-white font-bold text-lg w-8 text-center"
                                >
                                  {qty}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newQty = Math.min(maxQty, qty + 1);
                                    setAddonQuantities({ ...addonQuantities, [addon.id]: newQty });
                                  }}
                                  disabled={qty >= maxQty}
                                  data-testid={`addon-qty-plus-${addon.id}`}
                                  className="w-8 h-8 rounded-full bg-fuchsia-600/40 hover:bg-fuchsia-600 text-white font-bold text-lg disabled:opacity-30 disabled:cursor-not-allowed transition"
                                >
                                  +
                                </button>
                              </div>
                              <span className="text-xs text-fuchsia-300/80 ml-auto">
                                ${addon.price} × {qty} = <span className="text-white font-bold">${addon.price * qty}</span>
                              </span>
                            </div>
                          )}
                          
                          {/* Info Box */}
                          {addon.hasInfo && addon.infoText && (
                            <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-lg p-4 ml-8">
                              <div className="flex items-start gap-3">
                                <div className="text-2xl">🎬</div>
                                <div>
                                  <div className="text-sm font-semibold text-blue-300 mb-1">What's included:</div>
                                  <div className="text-sm text-gray-300 leading-relaxed">{addon.infoText}</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Date, Time, Form & Price */}
            <div className="space-y-6">
              {/* Calendar - Date Above Time */}
              <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Select Date</h3>
                <div
                  className="calendar-dark flex justify-center items-center"
                  data-testid="booking-calendar"
                  onPointerDown={() => trackFunnel('calendar_opened', gaPayload(), 'calendar_opened')}
                >
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={pickDate}
                    defaultMonth={new Date()}
                    fromMonth={new Date()}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    className="rounded-lg mx-auto"
                    modifiers={{
                      selected: selectedDate
                    }}
                    modifiersClassNames={{
                      selected: '!bg-purple-500 !text-white'
                    }}
                  />
                </div>
                {selectedDate && (
                  <div className="mt-3 p-2 bg-purple-600/20 rounded-lg border border-purple-500/20 text-center">
                    <p className="text-sm text-purple-300">
                      <span className="font-semibold text-white">{selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Time Selection - Directly Below Date */}
              {selectedDate ? (
              <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Clock className="text-purple-400" size={20} />
                  <h3 className="text-xl font-bold text-white">Select Time</h3>
                </div>
                {selectedEngineer && (() => {
                  const isToday = selectedDate.toDateString() === new Date().toDateString();
                  const next = timeSlots.find((s) => s.available && !blockedSlots.includes(s.time) && !isTimeSlotTooSoon(s.time));
                  if (!next) {
                    return (
                      <p data-testid="next-slot-none" className="mb-4 text-sm text-gray-400">
                        No open times {isToday ? 'left today' : 'this day'}. Try another date.
                      </p>
                    );
                  }
                  const picked = selectedTime === next.time;
                  return (
                    <button
                      type="button"
                      data-testid="next-slot-badge"
                      onClick={() => pickTime(next.time)}
                      className={`mb-4 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-full border transition-colors duration-200 ${
                        picked ? 'bg-purple-600 border-purple-500 text-white' : 'bg-purple-500/10 border-purple-500/40 text-purple-200 hover:bg-purple-500/20'
                      }`}
                    >
                      <span className="flex items-center gap-2 text-sm">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        Next available{isToday ? ' today' : ''}: <b className="text-white">{next.time}</b>
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide">{picked ? 'Selected' : 'Book it'}</span>
                    </button>
                  );
                })()}
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map((slot, idx) => {
                    const isTooSoon = isTimeSlotTooSoon(slot.time);
                    const isBlocked = blockedSlots.includes(slot.time);
                    const isDisabled = !slot.available || isTooSoon || !selectedEngineer || !selectedDate || isBlocked;

                    let tooltipText = '';
                    if (!selectedDate) tooltipText = 'Select a date first';
                    else if (!selectedEngineer) tooltipText = 'Select an engineer first';
                    else if (isBlocked) tooltipText = 'Time slot already booked';
                    else if (isTooSoon) tooltipText = 'Must book at least 2 hours in advance';

                    return (
                      <button
                        key={idx}
                        onClick={() => !isDisabled && pickTime(slot.time)}
                        disabled={isDisabled}
                        data-testid={`time-slot-${slot.time.replace(/\s|:/g, '')}`}
                        className={`p-3 rounded-lg text-sm font-medium transition-all ${
                          selectedTime === slot.time
                            ? 'bg-purple-600 text-white'
                            : isDisabled
                            ? 'bg-gray-800/20 text-gray-600 cursor-not-allowed'
                            : 'bg-black/40 text-gray-300 border border-purple-500/20 hover:border-purple-500/50'
                        }`}
                        title={tooltipText}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
                {!selectedDate && (
                  <p className="text-xs text-yellow-500 mt-2" data-testid="no-date-time-notice">
                    📅 Pick a date above to see available times
                  </p>
                )}
                {selectedDate && !selectedEngineer && (
                  <p className="text-xs text-yellow-500 mt-2">
                    ℹ️ Select an engineer to see available time slots
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  ⏰ Bookings must be made at least 2 hours in advance
                </p>
              </div>
              ) : (
                <div
                  data-testid="pick-date-hint"
                  className="bg-black/30 border border-dashed border-purple-500/30 rounded-xl p-6 text-center"
                >
                  <div className="text-3xl mb-2">📅</div>
                  <p className="text-sm text-purple-200 font-semibold">Pick a date to see available times</p>
                  <p className="text-xs text-gray-400 mt-1">Select a day in the calendar above and time slots will appear here.</p>
                </div>
              )}

              {/* Last Minute Warning */}
              {isLastMinuteBooking && (
                <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 backdrop-blur-sm border border-orange-500/50 rounded-xl p-4 flex items-start space-x-3">
                  <div className="text-orange-400 text-2xl">⚠️</div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Last-Minute Booking</h4>
                    <p className="text-gray-300 text-sm">
                      Booking 2-4 hours before session time incurs a <span className="font-bold text-orange-400">$20 rush fee</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Credits Checkout */}
              {artistPlan && selectedDuration && (
                <div data-testid="artist-plan-booking-box" className="rounded-lg border border-yellow-400/40 bg-yellow-400/10 p-3 text-sm">
                  {planUsable ? (
                    <label className="flex items-center gap-2 cursor-pointer text-yellow-100">
                      <input data-testid="use-plan-hours-checkbox" type="checkbox" checked={usePlanHours}
                        onChange={(e) => setUsePlanHours(e.target.checked)} className="accent-yellow-400" />
                      Use {selectedDuration} of my {artistPlan.hours_left} Artist Plan hours (no payment)
                    </label>
                  ) : (
                    <p className="text-yellow-100/80">
                      Artist Plan: {artistPlan.hours_left} hrs left.{' '}
                      {selectedEngineer === 1 ? 'Trap Spade is by special request - contact the studio.'
                        : selectedAddons.length ? 'Remove add-ons to book with plan hours.'
                        : Number(artistPlan.hours_left || 0) < selectedDuration ? 'Not enough plan hours for this length.' : 'Log in with your plan email to use hours.'}
                    </p>
                  )}
                </div>
              )}
              {!usePlanHours && calculatedPrice && formData.email && selectedDuration && localStorage.getItem('auth_token') &&
                (JSON.parse(localStorage.getItem('user_data') || '{}').email || '').toLowerCase() === formData.email.trim().toLowerCase() && (
                <CreditsCheckout
                  userEmail={formData.email}
                  hoursToBook={selectedDuration}
                  originalPrice={calculatedPrice.price}
                  onCreditsApplied={(credits) => setAppliedCredits(credits)}
                />
              )}

              {/* Payment Type Selection */}
              {calculatedPrice && (
                <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Payment Option</h3>
                  {calculatedPrice.hasTrapSpadeDeal ? (
                    <div className="p-4 bg-black/40 rounded-lg border-2 border-yellow-500/40">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-semibold">Full Payment Required</span>
                        <span className="text-yellow-400 font-bold">${calculatedPrice.price.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-yellow-400/80">This deal requires full payment upfront - no deposit option</p>
                    </div>
                  ) : (
                  <div className="space-y-3">
                    <label className={`flex items-start space-x-3 cursor-pointer p-4 bg-black/40 rounded-lg border-2 transition-all hover:bg-black/40 ${paymentType === 'deposit' ? 'border-purple-500' : 'border-purple-500/20'}`}>
                      <input
                        type="radio"
                        name="paymentType"
                        value="deposit"
                        data-testid="payment-type-deposit"
                        checked={paymentType === 'deposit'}
                        onChange={(e) => setPaymentType(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-semibold">Pay ${BOOKING_DEPOSIT} Deposit</span>
                          <span data-testid="deposit-option-amount" className="text-green-400 font-bold">${depositFor(calculatedPrice.price).toFixed(2)}</span>
                        </div>
                        <p className="text-sm text-gray-400">Hold your spot now. Remaining ${Math.max(0, calculatedPrice.price - depositFor(calculatedPrice.price)).toFixed(2)} is due at the studio on session day.</p>
                      </div>
                    </label>
                    
                    <label className={`flex items-start space-x-3 cursor-pointer p-4 bg-black/40 rounded-lg border-2 transition-all hover:bg-black/40 ${paymentType === 'full' ? 'border-purple-500' : 'border-purple-500/20'}`}>
                      <input
                        type="radio"
                        name="paymentType"
                        value="full"
                        data-testid="payment-type-full"
                        checked={paymentType === 'full'}
                        onChange={(e) => setPaymentType(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-semibold">Pay Full Amount</span>
                          <span className="text-green-400 font-bold">${calculatedPrice.price.toFixed(2)}</span>
                        </div>
                        <p className="text-sm text-gray-400">Pay complete session cost now - no balance due</p>
                      </div>
                    </label>
                  </div>
                  )}
                </div>
              )}

              {/* Price Summary */}
              {calculatedPrice && (
                <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 backdrop-blur-sm border border-green-500/30 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <DollarSign size={20} className="text-green-400" />
                    <span>Session Total</span>
                  </h3>
                  <div className="space-y-2">
                    {calculatedPrice.isTrapSpadeDeal ? (
                      <>
                        <div className="flex justify-between text-gray-400">
                          <span>{selectedDuration} Hours × <span className="line-through">${engineers.find(e => e.id === selectedEngineer).hourlyRate}/hr</span></span>
                          <span className="line-through">${calculatedPrice.original.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-purple-400 font-semibold">
                          <span className="flex items-center gap-1">
                            <span>⭐</span>
                            <span>{trapSpadeDeal?.dealName || 'SPECIAL DEAL'}</span>
                          </span>
                          <span>-${calculatedPrice.savings.toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-yellow-400 mt-1">
                          {trapSpadeDeal?.isUnlimited
                            ? 'Book as many as you want!'
                            : `Only ${calculatedPrice.trapSpadeDealRemaining} spots left at this price!`
                          }
                        </div>
                      </>
                    ) : calculatedPrice.isFourPlusOne ? (
                      <>
                        <div className="flex justify-between text-gray-400">
                          <span>{selectedDuration} Hours × <span className="line-through">${engineers.find(e => e.id === selectedEngineer).hourlyRate}/hr</span></span>
                          <span className="line-through">${calculatedPrice.original.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-yellow-300 font-semibold">
                          <span className="flex items-center gap-1">
                            <span>⚡</span>
                            <span>Book 4 Hours, Get 1 FREE</span>
                          </span>
                          <span>-${calculatedPrice.savings.toFixed(2)}</span>
                        </div>
                        <div className="text-[10px] text-yellow-200/80 -mt-1">
                          Limited-time deal · applied automatically on 5-hour sessions
                        </div>
                      </>
                    ) : calculatedPrice.isEarlyBird ? (
                      <>
                        <div className="flex justify-between text-gray-400">
                          <span>{selectedDuration} Hours × <span className="line-through">${engineers.find(e => e.id === selectedEngineer).hourlyRate}/hr</span></span>
                          <span className="line-through">${(engineers.find(e => e.id === selectedEngineer).hourlyRate * selectedDuration).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-amber-400 font-semibold">
                          <span className="flex items-center gap-1">
                            <span>☀️</span>
                            <span>Early Bird Special ($30/hr)</span>
                          </span>
                          <span>-${calculatedPrice.savings.toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between text-gray-400">
                          <span>{selectedDuration} Hours × ${engineers.find(e => e.id === selectedEngineer).hourlyRate}/hr</span>
                          {calculatedPrice.isBundle && calculatedPrice.savings > 0 ? (
                            <span className="line-through">${calculatedPrice.original.toFixed(2)}</span>
                          ) : (
                            <span>${(calculatedPrice.isBundle ? calculatedPrice.original - calculatedPrice.savings : calculatedPrice.priceBeforePromo).toFixed(2)}</span>
                          )}
                        </div>
                        {calculatedPrice.isBundle && calculatedPrice.savings > 0 && (
                          <div className="flex justify-between text-green-400 font-semibold">
                            <span>{selectedDuration === 8 ? '8-Hour Full Day Rate' : selectedDuration === 3 ? '3-Hour Bundle Deal' : `${selectedDuration}-Hour Package Deal`}</span>
                            <span>-${calculatedPrice.savings.toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}
                    {calculatedPrice.promoDiscount > 0 && (
                      <div className="flex justify-between text-emerald-400 font-semibold">
                        <span className="flex items-center gap-1">
                          <span>🎉</span>
                          <span>{promoStatus?.discount_percentage || 20}% Promo Discount</span>
                        </span>
                        <span>-${calculatedPrice.promoDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {calculatedPrice.pwaDiscount > 0 && (
                      <div className="flex justify-between text-purple-400 font-semibold">
                        <span className="flex items-center gap-1">
                          <span>📱</span>
                          <span>PWA App Discount (10%)</span>
                        </span>
                        <span>-${calculatedPrice.pwaDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {calculatedPrice.referralDiscountAmount > 0 && (
                      <div className="flex justify-between text-green-400 font-semibold">
                        <span className="flex items-center gap-1">
                          <span>🎁</span>
                          <span>Referral Code Discount ({referralDiscount?.discount_percentage || 20}%)</span>
                        </span>
                        <span>-${calculatedPrice.referralDiscountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {calculatedPrice.discountCodeAmount > 0 && (
                      <div className="flex justify-between text-emerald-400 font-semibold" data-testid="discount-code-line">
                        <span className="flex items-center gap-1">
                          <span>Discount Code ({discountResult?.discount_percent}%)</span>
                        </span>
                        <span>-${calculatedPrice.discountCodeAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {calculatedPrice.addonsTotal > 0 && (
                      <div className="flex justify-between text-blue-400 font-semibold">
                        <span>Add-On Services</span>
                        <span>+${calculatedPrice.addonsTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {calculatedPrice.lastMinuteFee > 0 && (
                      <div className="flex justify-between text-orange-400 font-semibold">
                        <span>Last-Minute Booking Fee</span>
                        <span>+${calculatedPrice.lastMinuteFee}</span>
                      </div>
                    )}
                    {!(bonusHoursDeal?.is_active && selectedDuration >= (Number(bonusHoursDeal?.tiers?.[0]?.min_hours) || 4)) && dealFreeHours(selectedDuration) > 0 && (
                      <div data-testid="weekday-bonus-row" className="flex justify-between items-center bg-fuchsia-600/15 border border-fuchsia-500/30 rounded-lg p-3 mt-2 text-sm">
                        <span className="text-fuchsia-200 font-semibold">Mon-Wed Deal: +{dealFreeHours(selectedDuration)} FREE Hour</span>
                        <span className="text-white font-bold">{selectedDuration + dealFreeHours(selectedDuration)} hrs total</span>
                      </div>
                    )}
                    {bonusHoursDeal?.is_active && selectedDuration >= (Number(bonusHoursDeal?.tiers?.[0]?.min_hours) || 4) && (
                      <div
                        className="flex justify-between items-center bg-gradient-to-r from-fuchsia-600/20 to-pink-600/20 border border-fuchsia-500/30 rounded-lg p-3 mt-2"
                        data-testid="bonus-included-row"
                      >
                        <span className="flex items-center gap-2 text-fuchsia-200 font-semibold text-sm">
                          <span className="text-lg">🎬</span>
                          <span>
                            Bonus Included:{' '}
                            <span className="text-white">
                              {dealFreeHours(selectedDuration)
                                ? `1 FREE Reel + ${dealFreeHours(selectedDuration)} FREE Hour (${selectedDuration + dealFreeHours(selectedDuration)} hrs total)`
                                : (bonusHoursDeal?.tiers?.[0]?.bonus || '1 FREE Reel')}
                            </span>
                          </span>
                        </span>
                        <span className="text-xs text-fuchsia-300/80">Voucher on confirmation</span>
                      </div>
                    )}
                    <div className="border-t border-purple-500/20 pt-2 mt-2">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-bold text-lg">Session Total</span>
                        <span data-testid="session-total" className="text-2xl font-bold text-green-400">${calculatedPrice.price.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-300 bg-purple-900/20 rounded p-2">
                        <span>{paymentType === 'deposit' ? 'Due now (deposit)' : 'Due now (full payment)'}</span>
                        <span data-testid="due-now-amount" className="font-semibold">${(paymentType === 'deposit' ? depositFor(calculatedPrice.price) : calculatedPrice.price).toFixed(2)}</span>
                      </div>
                      {paymentType === 'deposit' && (
                        <div className="flex justify-between text-sm text-gray-400 px-2 pt-2">
                          <span>Remaining balance</span>
                          <span data-testid="remaining-balance-amount">${Math.max(0, calculatedPrice.price - depositFor(calculatedPrice.price)).toFixed(2)}</span>
                        </div>
                      )}
                      {paymentType === 'deposit' && (
                        <p data-testid="balance-due-note" className="text-xs text-gray-500 mt-2 px-2">{BALANCE_DUE_NOTE}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Booking Form */}
              <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Your Details</h3>
                
                {/* Sign Up Promotion */}
                {!user && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🎁</span>
                      <div>
                        <p className="text-white font-bold text-sm mb-1">Have an account? Sign in to save 20%!</p>
                        <p className="text-purple-200 text-xs mb-2">Members get their referral code instantly + loyalty credits</p>
                        <button
                          type="button"
                          onClick={() => window.dispatchEvent(new CustomEvent('openAuthModal'))}
                          className="text-purple-300 hover:text-purple-100 text-xs font-semibold underline"
                        >
                          Sign In or Create Free Account →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleBooking} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    data-testid="booking-name-input"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    data-testid="booking-email-input"
                    required
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      // Store email in localStorage for PWA discount tracking
                      if (e.target.value) {
                        localStorage.setItem('user_email', e.target.value);
                      }
                    }}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    data-testid="booking-phone-input"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />

                  <textarea
                    placeholder="Additional notes or requests..."
                    data-testid="booking-notes-input"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none"
                  ></textarea>
                  
                  {availableCredits > 0 && (
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                      <p className="text-green-400 font-semibold">
                        🎉 You have {availableCredits} referral credit{availableCredits > 1 ? 's' : ''} available!
                      </p>
                      <p className="text-sm text-gray-300 mt-1">
                        20% discount will be automatically applied to this booking
                      </p>
                    </div>
                  )}
                  
                  {availableCredits === 0 && (
                    <div>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Referral Code (Optional) - Get 20% off!"
                          data-testid="booking-referral-input"
                          value={formData.referralCode}
                          onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                          className={`w-full px-4 py-3 bg-black/40 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 ${
                            referralDiscount?.valid 
                              ? 'border-green-500 focus:border-green-500 focus:ring-green-500/20' 
                              : referralDiscount?.valid === false 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                : 'border-purple-500/20 focus:border-purple-500 focus:ring-purple-500/20'
                          }`}
                        />
                        {isValidatingReferral && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                        {referralDiscount?.valid && !isValidatingReferral && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                            <CheckCircle size={20} />
                          </div>
                        )}
                      </div>
                      {referralDiscount?.valid && (
                        <p className="text-xs text-green-400 mt-2 font-semibold">✓ {referralDiscount.discount_percentage}% discount applied!</p>
                      )}
                      {referralDiscount?.valid === false && formData.referralCode.length >= 4 && (
                        <p className="text-xs text-red-400 mt-2">{referralDiscount.message || 'Invalid referral code'}</p>
                      )}
                      {!referralDiscount && (
                        <p className="text-xs text-gray-400 mt-2">First-time booking only</p>
                      )}
                    </div>
                  )}
                  
                  {/* Discount Code Input */}
                  <div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Discount Code"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        data-testid="discount-code-input"
                        className={`w-full px-4 py-3 bg-black/40 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 ${
                          discountResult?.valid 
                            ? 'border-emerald-500 focus:border-emerald-500 focus:ring-emerald-500/20' 
                            : discountResult?.valid === false 
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-purple-500/20 focus:border-purple-500 focus:ring-purple-500/20'
                        }`}
                      />
                      {isValidatingDiscount && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      {discountResult?.valid && !isValidatingDiscount && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500">
                          <CheckCircle size={20} />
                        </div>
                      )}
                    </div>
                    {discountResult?.valid && (
                      <p className="text-xs text-emerald-400 mt-2 font-semibold" data-testid="discount-applied-msg">{discountResult.message}</p>
                    )}
                    {discountResult?.valid === false && discountCode.length >= 4 && (
                      <p className="text-xs text-red-400 mt-2">{discountResult.message}</p>
                    )}
                  </div>

                  {/* Consent: terms required; SMS optional and separate (A2P 10DLC) */}
                  <div className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-2" data-testid="booking-consent-card">
                    <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-200">
                      <input
                        type="checkbox"
                        data-testid="terms-consent-checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="w-4 h-4 shrink-0 rounded border-white/30 bg-black/40 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                        required
                      />
                      <span>
                        I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">Terms</a> &amp; <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">Privacy Policy</a> <span className="text-red-400">(all sales final)</span>
                      </span>
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-200">
                      <input
                        type="checkbox"
                        data-testid="sms-consent-checkbox"
                        checked={agreedToSms}
                        onChange={(e) => setAgreedToSms(e.target.checked)}
                        className="w-4 h-4 shrink-0 rounded border-white/30 bg-black/40 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                      />
                      <span>Text me booking updates <span className="text-gray-500">(optional)</span></span>
                    </label>
                    <p className="text-[11px] leading-snug text-gray-500 pl-6" data-testid="booking-consent-fineprint">
                      Texts from High Quality Studios about confirmations, approvals and reminders. Frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of booking. You'll get a receipt email now and a confirmation email with the studio address once your session is confirmed.
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    data-testid="booking-submit-btn"
                    disabled={isSubmitting || !agreedToTerms}
                    className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={20} />
                        <span>
                          {usePlanHours && planUsable ? 'Book with Plan Hours'
                            : calculatedPrice && !appliedCredits ? `Continue to Secure Payment · $${depositAmountFor(calculatedPrice.price).toFixed(2)} now`
                            : 'Confirm Booking'}
                        </span>
                      </>
                    )}
                  </button>
                  {!agreedToTerms && (
                    <p data-testid="terms-required-hint" className="text-xs text-gray-500 text-center -mt-2">Tick the Terms box above to continue.</p>
                  )}
                </form>
              </div>

              {/* Package Deals */}
              <div id="bundles" className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="text-2xl">📦</div>
                    <h3 className="text-xl font-bold text-white">Package Deals</h3>
                  </div>
                  <span className="text-xs text-purple-400 font-semibold">Save More</span>
                </div>
                <div className="space-y-3">
                  {packageDeals.map((pkg) => (
                    <div
                      key={pkg.id}
                      className={`p-4 rounded-lg border-2 ${
                        pkg.highlight
                          ? 'border-green-500 bg-green-600/10'
                          : 'border-purple-500/20 bg-black/20'
                      }`}
                    >
                      {pkg.badgeText && (
                        <div className="mb-2">
                          <span className="text-xs font-semibold px-2 py-1 rounded bg-green-500/20 text-green-400 border border-green-500/30">
                            ✨ {pkg.badgeText}
                          </span>
                        </div>
                      )}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-bold text-white text-lg mb-2">{pkg.name}</h4>
                          <div className="space-y-1">
                            {pkg.includes.map((item, idx) => (
                              <div key={idx} className="flex items-center space-x-2 text-sm">
                                <span className="text-green-400">✓</span>
                                <span className="text-gray-300">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-2xl font-bold text-green-400">${pkg.price}</div>
                          <div className="text-xs text-gray-400 mt-1">Total Package</div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <button
                          onClick={() => {
                            setSelectedPackage(pkg);
                            setShowPackageModal(true);
                          }}
                          className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all text-sm"
                        >
                          Book This Package
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <p className="text-xs text-blue-300">
                    <strong>Note:</strong> Package deals require a deposit. Fill out the booking form and we&apos;ll confirm availability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Package Booking Modal */}
      {showPackageModal && selectedPackage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-purple-900 to-blue-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-purple-500/20">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">{selectedPackage.name}</h3>
                  <p className="text-purple-300">Package Deal Inquiry</p>
                </div>
                <button
                  onClick={() => {
                    setShowPackageModal(false);
                    setSelectedPackage(null);
                    setPackageFormData({ name: '', email: '', phone: '', requestedDate: '', requestedTime: '', notes: '' });
                  }}
                  className="text-gray-400 hover:text-white transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Package Details */}
              <div className="bg-black/30 rounded-lg p-4 mb-6 border border-purple-500/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-300 font-semibold">Package Includes:</span>
                  <span className="text-3xl font-bold text-green-400">${selectedPackage.price}</span>
                </div>
                <ul className="space-y-2">
                  {selectedPackage.includes.map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-gray-300">
                      <span className="text-green-400">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Booking Form */}
              <form onSubmit={handlePackageBooking} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="package_name"
                    id="package_name"
                    placeholder="Your Name *"
                    required
                    value={packageFormData.name}
                    onChange={(e) => setPackageFormData({ ...packageFormData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  />
                  <input
                    type="email"
                    name="package_email"
                    id="package_email"
                    placeholder="Email Address *"
                    required
                    value={packageFormData.email}
                    onChange={(e) => setPackageFormData({ ...packageFormData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  />
                </div>

                <input
                  type="tel"
                  name="package_phone"
                  id="package_phone"
                  placeholder="Phone Number *"
                  required
                  value={packageFormData.phone}
                  onChange={(e) => setPackageFormData({ ...packageFormData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="date"
                    name="package_date"
                    id="package_date"
                    required
                    value={packageFormData.requestedDate}
                    onChange={(e) => setPackageFormData({ ...packageFormData, requestedDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  />
                  <select
                    name="package_time"
                    id="package_time"
                    required
                    value={packageFormData.requestedTime}
                    onChange={(e) => setPackageFormData({ ...packageFormData, requestedTime: e.target.value })}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                    <option value="6:00 PM">6:00 PM</option>
                    <option value="7:00 PM">7:00 PM</option>
                    <option value="8:00 PM">8:00 PM</option>
                    <option value="9:00 PM">9:00 PM</option>
                  </select>
                </div>

                <textarea
                  name="package_notes"
                  id="package_notes"
                  placeholder="Additional requests or notes (optional)"
                  rows={3}
                  value={packageFormData.notes}
                  onChange={(e) => setPackageFormData({ ...packageFormData, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none resize-none"
                ></textarea>

                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-300 text-sm">
                    <strong>📝 Note:</strong> A $100 deposit is required to secure your package deal booking. We&apos;ll contact you within 24 hours to confirm availability and finalize details.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isPackageSubmitting}
                  className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPackageSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      <span>Pay $100 Deposit & Submit Inquiry</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BookingSection;